import React, { useState, useEffect, useCallback } from 'react';
import { User, Home, RefreshCw } from 'lucide-react';
import { GameMode, GAME_MODES } from '../utilities/gameMode';
import { trackLandingFormSubmit } from '../utilities/analytics';
import humanId from 'human-id';

/**
 * Game URL from environment or production fallback
 */
const GAME_URL = import.meta.env.VITE_GAME_URL || 'https://game.shengji.io';

/**
 * Human-ID options configured for room names
 */
const roomIdOptions = {
  separator: '-',
  capitalize: false,
  adjectiveCount: 1,
  addAdverb: true,
};

/**
 * Validation regex for room names
 * Must be URL-safe: ASCII letters, numbers, hyphens, underscores only
 */
const ROOM_NAME_REGEX = /^[a-zA-Z0-9_-]+$/;

/**
 * LandingForm component for creating/joining game rooms
 */
export function LandingForm() {
  const [displayName, setDisplayName] = useState('');
  const [roomName, setRoomName] = useState('');
  const [gameMode, setGameMode] = useState<GameMode>(GAME_MODES.SHENGJI);
  const [roomNameError, setRoomNameError] = useState('');

  /**
   * Generates a random room name using human-id
   */
  const generateRandomRoomName = useCallback(() => {
    return humanId(roomIdOptions);
  }, []);

  /**
   * Validates room name input
   * Room must be non-empty and contain only URL-safe characters
   */
  const validateRoomName = useCallback((name: string): boolean => {
    if (!name) {
      // Empty is valid (will generate random)
      setRoomNameError('');
      return true;
    }
    if (!ROOM_NAME_REGEX.test(name)) {
      setRoomNameError('Letters, numbers, hyphens, and underscores only');
      return false;
    }
    setRoomNameError('');
    return true;
  }, []);

  /**
   * Handle room name input change with validation
   */
  const handleRoomNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRoomName(value);
    if (value) {
      validateRoomName(value);
    } else {
      setRoomNameError('');
    }
  };

  /**
   * Handle regenerate room name button click
   */
  const handleRegenerateRoomName = useCallback(() => {
    const newName = generateRandomRoomName();
    setRoomName(newName);
    setRoomNameError('');
  }, [generateRandomRoomName]);

  /**
   * Handle game mode selection
   */
  const handleGameModeSelect = (mode: GameMode) => {
    setGameMode(mode);
  };

  /**
   * Handle form submission
   * Redirects to game URL with room path and hash fragment for username
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate display name
    if (!displayName.trim()) {
      return;
    }

    // Validate room name (or generate random if empty)
    if (!validateRoomName(roomName)) {
      return;
    }

    // Use provided room name or generate random
    const finalRoomName = roomName.trim() || generateRandomRoomName();

    // Track submission before redirect
    trackLandingFormSubmit({
      username: displayName.trim(),
      room: finalRoomName,
      gameMode,
    });

    // Construct redirect URL
    // Room name goes in path (already validated as URL-safe)
    // Username goes in hash (supports UTF-8/Chinese via encodeURIComponent)
    const redirectUrl = `${GAME_URL}/${finalRoomName}#username=${encodeURIComponent(displayName.trim())}&mode=${gameMode}`;

    window.location.href = redirectUrl;
  };

  /**
   * Keyboard navigation for game mode toggle
   * Space toggles between modes when toggle is focused
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === ' ' && document.activeElement?.tagName === 'BUTTON') {
        const modes: GameMode[] = [
          GAME_MODES.SHENGJI,
          GAME_MODES.FINDING_FRIENDS,
        ];
        const currentIndex = modes.indexOf(gameMode);
        const nextIndex = (currentIndex + 1) % modes.length;
        setGameMode(modes[nextIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameMode]);

  const isFindingFriends = gameMode === GAME_MODES.FINDING_FRIENDS;

  // Determine what's missing for tooltip
  const getTooltipMessage = () => {
    const missing = [];
    if (!displayName.trim()) missing.push('Display Name');
    if (!roomName && roomNameError) missing.push('valid Room Name');
    if (missing.length === 0) return 'Join a room';
    return `Please provide: ${missing.join(', ')}`;
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full max-w-sm bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-gray-200 shadow-lg"
    >
      {/* Display Name Input */}
      <div className="mb-3">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <User className="h-4 w-4 text-gray-400" />
          </div>
          <input
            id="displayName"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Display Name"
            className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent text-gray-900 text-sm placeholder:text-gray-500"
            required
          />
        </div>
      </div>

      {/* Room Name Input */}
      <div className="mb-3">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Home className="h-4 w-4 text-gray-400" />
          </div>
          <input
            id="roomName"
            type="text"
            value={roomName}
            onChange={handleRoomNameChange}
            placeholder="Room Name"
            className={`block w-full pl-9 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-gray-900 text-sm placeholder:text-gray-500 ${
              roomNameError
                ? 'border-red-300 focus:ring-red-400'
                : 'border-gray-300 focus:ring-gray-400'
            }`}
          />
          <button
            type="button"
            onClick={handleRegenerateRoomName}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            title="Generate new room name"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
        {roomNameError && (
          <p className="mt-1 text-xs text-red-600">{roomNameError}</p>
        )}
      </div>

      {/* Game Mode Selection - Tabs */}
      <div className="mb-4">
        <div className="flex rounded-lg border border-gray-300 p-1 bg-gray-50">
          <button
            type="button"
            onClick={() => handleGameModeSelect(GAME_MODES.SHENGJI)}
            className={`flex-1 py-2.5 px-3 text-sm rounded-md transition-all ${
              !isFindingFriends
                ? 'bg-white text-gray-900 shadow-md font-semibold'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            Shengji
          </button>
          <button
            type="button"
            onClick={() => handleGameModeSelect(GAME_MODES.FINDING_FRIENDS)}
            title="Up to 7 players"
            className={`flex-1 py-2.5 px-3 text-sm rounded-md transition-all ${
              isFindingFriends
                ? 'bg-white text-gray-900 shadow-md font-semibold'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            Finding Friends
          </button>
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        title={getTooltipMessage()}
        className="w-full flex items-center justify-center px-6 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600 transition-colors cursor-pointer"
      >
        Join a room
      </button>
    </form>
  );
}
